import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { code: "", discount_type: "percent", discount_value: 10, min_order: 0, max_uses: 0, is_active: true };

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/coupons").then(r => setCoupons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/admin/coupons", editing);
      toast.success("Created"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}?`)) return;
    await api.delete(`/admin/coupons/${c.id}`);
    load();
  };

  return (
    <div data-testid="admin-coupons-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div><p className="eyebrow mb-1">Offers</p><h1 className="font-hero text-3xl">Coupons</h1></div>
        <button className="btn-primary" onClick={() => { setEditing({ ...empty }); setOpen(true); }}><Plus size={14} className="mr-2" /> New coupon</button>
      </div>
      <div className="bg-white rounded-2xl border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left font-label text-[11px] uppercase tracking-[0.22em] text-charcoal/70 border-b border-border">
              <th className="py-3 px-4">Code</th><th>Discount</th><th>Min order</th><th>Uses</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map(c => (
              <tr key={c.id} className="border-b border-border last:border-b-0">
                <td className="py-3 px-4 font-cormorant text-lg">{c.code}</td>
                <td>{c.discount_type === "percent" ? `${c.discount_value}%` : `₹${c.discount_value}`}</td>
                <td>₹{c.min_order || 0}</td>
                <td>{c.used_count}{c.max_uses > 0 ? ` / ${c.max_uses}` : ""}</td>
                <td>{c.is_active ? <span className="text-emerald-600 text-xs uppercase">Active</span> : <span className="text-charcoal/70 text-xs uppercase">Paused</span>}</td>
                <td className="text-right px-4"><button onClick={() => remove(c)} className="text-rose" aria-label="delete"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : setOpen(false))}>
        <DialogContent className="bg-cream max-w-lg">
          <DialogHeader><DialogTitle className="font-hero text-2xl">New coupon</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><label className="eyebrow">Code</label><Input value={editing.code} onChange={e => setEditing({ ...editing, code: e.target.value.toUpperCase() })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow">Type</label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={editing.discount_type} onChange={e => setEditing({ ...editing, discount_type: e.target.value })}>
                    <option value="percent">Percent</option>
                    <option value="flat">Flat</option>
                  </select>
                </div>
                <div><label className="eyebrow">Value</label><Input type="number" value={editing.discount_value} onChange={e => setEditing({ ...editing, discount_value: Number(e.target.value) })} /></div>
                <div><label className="eyebrow">Min order</label><Input type="number" value={editing.min_order} onChange={e => setEditing({ ...editing, min_order: Number(e.target.value) })} /></div>
                <div><label className="eyebrow">Max uses (0 = ∞)</label><Input type="number" value={editing.max_uses} onChange={e => setEditing({ ...editing, max_uses: Number(e.target.value) })} /></div>
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button className="btn-outline" onClick={() => setOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={save}>Create</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoupons;
